import { useState, useEffect } from "react";
import { ArrowRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Reveal } from "@/components/Reveal";

export const SuccessStoryCard = ({ story, delay = 0 }) => {
  const [open, setOpen] = useState(false);

  // Pause Lenis smooth-scroll while the modal is open so the wheel/touch
  // events scroll the biography instead of the page behind it. The cleanup
  // guarantees Lenis is always resumed (never a permanent scroll lock).
  useEffect(() => {
    const lenis = window.__lenis;
    if (lenis) {
      if (open) lenis.stop();
      else lenis.start();
    }
    return () => {
      window.__lenis?.start();
    };
  }, [open]);

  return (
  <Reveal delay={delay}>
    <Dialog open={open} onOpenChange={setOpen}>
      <div
        data-testid={`success-story-${story.id}`}
        className="group bg-ink-surface border border-white/10 h-full flex flex-col overflow-hidden transition-colors duration-300 hover:border-burnt/50"
      >
        <div className="relative overflow-hidden aspect-[16/10]">
          <img
            src={story.image}
            alt={story.title}
            style={{ objectPosition: story.imgPosition || "center" }}
            className={`w-full h-full transition-transform duration-700 group-hover:scale-105 ${
              story.imgFit === "contain" ? "object-contain" : "object-cover"
            }`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink-surface via-transparent to-transparent" />
          <span className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.2em] text-white bg-black/55 backdrop-blur px-2.5 py-1">
            {story.tag}
          </span>
        </div>
        <div className="p-7 flex flex-col flex-1">
          <h3 className="font-heading text-xl md:text-2xl font-semibold text-white leading-tight">
            {story.title}
          </h3>
          {story.person && (
            <p className="mt-1.5 text-sm text-gold">{story.person}</p>
          )}
          <p className="mt-3 text-zinc-400 leading-relaxed flex-1">
            {story.summary}
          </p>
          <DialogTrigger asChild>
            <button
              data-testid={`success-story-read-${story.id}`}
              className="group/btn mt-6 inline-flex items-center gap-2 text-burnt font-medium text-sm self-start"
            >
              Read More
              <ArrowRight
                size={15}
                className="transition-transform group-hover/btn:translate-x-1"
              />
            </button>
          </DialogTrigger>
        </div>
      </div>

      <DialogContent
        data-lenis-prevent
        className="bg-ink-elevated border-white/10 text-white max-w-2xl max-h-[88vh] overflow-y-auto overscroll-contain p-0"
      >
        <div className="relative aspect-[16/9]">
          <img
            src={story.image}
            alt={story.title}
            style={{ objectPosition: story.imgPosition || "center" }}
            className={`w-full h-full ${
              story.imgFit === "contain" ? "object-contain" : "object-cover"
            }`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink-elevated to-transparent" />
          <span className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.2em] text-white bg-black/55 backdrop-blur px-2.5 py-1">
            {story.tag}
          </span>
        </div>
        <div className="p-7 md:p-9 pt-2">
          {story.caption && (
            <p className="text-xs italic text-zinc-500 mb-4">{story.caption}</p>
          )}
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl md:text-3xl font-semibold text-white text-left leading-tight">
              {story.title}
            </DialogTitle>
            <DialogDescription className="text-left text-gold">
              {story.person ? story.person : `${story.tag} · A story beyond basketball`}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-5 space-y-4">
            {story.body.map((p, i) => (
              <p key={i} className="text-zinc-400 leading-relaxed">
                {p}
              </p>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  </Reveal>
  );
};

export default SuccessStoryCard;
